/**
 * Component: Collapsible Block
 * -----------------------------------------------------------------------------
 * - A responsive, collapsible component with optional mobile-only behavior
 * - Designed to use native accordions with `<details>` and `<summary>`
 * - This is intended to be imported into a given section, followed by
 * customElements.define('...')
 *
 * @namespace CollapsibleBlock
 *
 * @example
 * <collapsible-block class="collapsible-block" mobile-only="true">
 *   <details class="collapsible-block__details" js-details-element>
 *     <summary class="collapsible-block__heading">Section Header</summary>
 *     <div class="collapsible-block__content">Section Content</div>
 *   </details>
 * </collapsible-block>
 */

import breakpoints from '~/helpers/breakpoints'
import { parseAttributeValue } from '~/helpers/dom'

/**
 * Create CollapsibleBlock Class.
 */
export default class CollapsibleBlock extends HTMLElement {
  static observedAttributes = ['mobile-only']

  constructor() {
    super()

    this.setDefaultState()
  }

  /**
   * Set the default state.
   */
  setDefaultState() {
    this.mobileOnly = parseAttributeValue(this.getAttribute('mobile-only'))
    this.isMobile = !window.matchMedia(`(min-width: ${breakpoints.large})`)
      .matches
  }

  /**
   * Set selectors
   */
  setSelectors() {
    this.selectors = {
      detailsElement: this.querySelector('[js-details-element]'),
    }
  }

  /**
   * The element is now defined on the page, so run functionality.
   */
  connectedCallback() {
    this.setSelectors()
    this.updateOpenState()
    this.setEventListeners()
  }

  /**
   * Set Event Listeners.
   */
  setEventListeners() {
    if (!this.mobileOnly) {
      return
    }

    /**
     * Screen has been resized.
     */
    mint.EventBus.on('mint:resize', (response) => {
      this.isMobile = response.screen?.mobile
      this.updateOpenState()
    })
  }

  /**
   * Update the accordion state based on the screen size.
   * - set `open` state for the accordions on desktop if `mobile-only` is set.
   */
  updateOpenState() {
    if (!this.mobileOnly) {
      return
    }

    this.isMobile
      ? this.selectors.detailsElement?.removeAttribute('open')
      : this.selectors.detailsElement?.setAttribute('open', '')
  }
}
